import { useEffect, useState } from "react";
import { StarIcon, PlusIcon, XIcon, TrophyIcon, AlertTriangleIcon } from "lucide-react";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Award } from "@type/cv";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";

interface Props {
  isEditing: boolean;
  isDisabled: boolean;
  data: Award[];
  onChange: (values: Award[]) => void;
  onEdit: () => void;
}

export function Awards(props: Props) {
  const [awards, setAward] = useState<Award[]>([]);

  useEffect(() => {
    setAward(props.data);
  }, []);

  useEffect(() => {
    props.onChange(awards);
  }, [awards]);

  const removeAward = (index: number) => {
    setAward(awards.filter((_, i) => i !== index));
  };

  const handleAwardChange = (
    index: number,
    field: keyof Award,
    value: string
  ) => {
    const updatedAwards = [...awards];
    updatedAwards[index][field] = value;
    setAward(updatedAwards);
  };

  const addAward = () => {
    setAward([
      ...awards,
      {
        name: "",
        institution: "",
        date: "",
        description: "",
      },
    ]);
  };

  return (
    <div className="p-6">
      <div>
        {!props.isEditing && awards.length === 0 && (
          <Button
            onClick={props.onEdit}
            disabled={props.isDisabled}
            variant="ghost"
            className="flex items-center text-blue-500 hover:text-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Award
          </Button>
        )}

        {awards.map((award, index) => (
          <div key={index}>
            {props.isEditing ? (
              <div className="space-y-4 mb-6">
                <button
                  onClick={() => removeAward(index)}
                  className="float-right text-red-500 hover:text-red-700"
                >
                  <XIcon className="h-5 w-5" id={`7lp42m_${index}`} />
                </button>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  id={`7l4zwz_${index}`}
                >
                  <div id={`jqfjrq_${index}`}>
                    <label
                      className="text-sm text-gray-600 mb-1 block"
                      id={`ng9awf_${index}`}
                    >
                      Award Title
                    </label>
                    <Input
                      value={award.name}
                      onChange={(e) =>
                        handleAwardChange(index, "name", e.target.value)
                      }
                      className="border-gray-300 text-black"
                      id={`ixp4lb_${index}`}
                    />
                    {award.name === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Award title is required.
                        </p>
                      </div>
                    )}
                  </div>
                  <div id={`0hktx7_${index}`}>
                    <label
                      className="text-sm text-gray-600 mb-1 block"
                      id={`3bad2w_${index}`}
                    >
                      Date
                    </label>
                    <Input
                      value={award.date}
                      onChange={(e) =>
                        handleAwardChange(index, "date", e.target.value)
                      }
                      className="border-gray-300 text-black"
                      id={`7wd73n_${index}`}
                    />
                    {award.date === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Date is required.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div id={`b3oybh_${index}`}>
                  <label
                    className="text-sm text-gray-600 mb-1 block"
                    id={`sfxpv4_${index}`}
                  >
                    Organization
                  </label>
                  <Input
                    value={award.institution}
                    onChange={(e) =>
                      handleAwardChange(index, "institution", e.target.value)
                    }
                    className="border-gray-300 text-black"
                    id={`4cfhdn_${index}`}
                  />
                  {award.institution === "" && (
                    <div className="flex flex-row items-center mt-2 space-x-1">
                      <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                      <p className="text-red-600 text-sm">
                        Organization is required.
                      </p>
                    </div>
                  )}
                </div>
                <div id={`kzcvz9_${index}`}>
                  <label
                    className="text-sm text-gray-600 mb-1 block"
                    id={`3221zw_${index}`}
                  >
                    Description
                  </label>
                  <Textarea
                    value={award.description}
                    onChange={(e) =>
                      handleAwardChange(index, "description", e.target.value)
                    }
                    className="border-gray-300 text-black"
                    rows={3}
                    id={`x7idso_${index}`}
                  />
                </div>
              </div>
            ) : (
              <div key={index} className={cn("relative pl-12", {
                "pb-8": index < awards.length - 1,
              })}>
                {/* Timeline line - only show for non-last items */}
                {index < awards.length - 1 && (
                  <div
                    className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"
                    style={{ backgroundColor: "#fef3c7" }}
                  ></div>
                )}

                {/* Icon */}
                <div className="absolute left-0 top-1">
                  <div
                    className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                    style={{
                      backgroundColor: "#fef3c7",
                      border: "1.81818px solid #f59e0b",
                    }}
                  >
                    <TrophyIcon
                      className="h-3 w-3 text-blue-600 dark:text-blue-400"
                      style={{ color: "#d97706" }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full">
                      {award.name}
                    </h5>
                    {award.date && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                        {award.date}
                      </span>
                    )}
                  </div>
                  {award.institution && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {award.institution}
                    </p>
                  )}
                  {award.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {award.description}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {props.isEditing && (
        <Button
          onClick={addAward}
          variant="ghost"
          className="mt-4 flex items-center text-blue-500 hover:text-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Award
        </Button>
      )}
    </div>
  );
}
